import React, { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check } from 'lucide-react';
import { useOnboardingStore } from '@/hooks/use-onboarding-store';

const PaymentSuccessPage = () => {
  const [isVerifying, setIsVerifying] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [isNocodePayment, setIsNocodePayment] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setCurrentStep } = useOnboardingStore();
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id');
  const reference = searchParams.get('reference');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) return;

      try {
        const { data, error } = await supabase.functions.invoke('verify-stripe-payment', {
          body: { sessionId },
        });

        if (error) {
          console.error('Error verifying payment:', error);
          return;
        }

        if (data.paymentStatus === 'paid') {
          setPaymentVerified(true);
          setCustomerEmail(data.customerEmail);
          
          // Check if this is a nocode onboarding payment
          const isFromNocode = data.metadata?.source === 'nocode_onboarding';
          setIsNocodePayment(isFromNocode);
          
          // If it's a nocode payment, redirect to onboarding thank you step
          if (isFromNocode) {
            setTimeout(() => {
              setCurrentStep(12); // Set to thank you step
              navigate('/nocode/onboarding');
            }, 2000); // Give user time to see success message
          }
        }
      } catch (err) {
        console.error('Error during payment verification:', err);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow px-4 py-16 bg-luxury-50">
        <div className="container mx-auto max-w-3xl">
          <div className="bg-white rounded-xl shadow-luxury p-8 text-center">
            {isVerifying ? (
              <div className="py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto"></div>
                <p className="mt-4 text-lg">Verifying your payment...</p>
              </div>
            ) : paymentVerified ? (
              <div className="py-8">
                <div className="rounded-full bg-green-100 p-3 w-16 h-16 mx-auto">
                  <Check className="w-10 h-10 text-green-600 mx-auto" />
                </div>
                <h1 className="text-3xl font-bold mt-6 mb-4">Payment Successful!</h1>
                <p className="text-lg text-gray-600 mb-6">
                  {isNocodePayment 
                    ? "Thank you for enrolling in the No-Code Mastery course! Redirecting you to complete your onboarding..."
                    : "Thank you for your order. We've received your payment and will be in touch shortly."
                  }
                </p>
                
                {!isNocodePayment && (
                  <div className="bg-luxury-50 p-6 rounded-lg mb-8 mx-auto max-w-md">
                    <h3 className="text-lg font-semibold mb-2">What happens next?</h3>
                    <ol className="text-left text-gray-700 space-y-3">
                      <li className="flex items-start">
                        <span className="bg-gold/20 text-gold rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5">1</span>
                        <span>Our team will review your project details</span>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-gold/20 text-gold rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5">2</span>
                        <span>We'll contact you at <strong>{customerEmail}</strong> within 24 hours</span>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-gold/20 text-gold rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5">3</span>
                        <span>We'll schedule a kickoff meeting to discuss your project in detail</span>
                      </li>
                    </ol>
                  </div>
                )}
                
                {isNocodePayment && (
                  <div className="bg-blue-50 p-6 rounded-lg mb-8 mx-auto max-w-md">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-blue-800 font-medium">Completing your enrollment...</p>
                  </div>
                )}
                
                {!isNocodePayment && (
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <Link to="/">
                      <Button className="bg-gold hover:bg-gold/90 text-white w-full">
                        Return to Home
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-8">
                <h1 className="text-2xl font-bold mb-4">Payment Verification Failed</h1>
                <p className="text-gray-600 mb-6">
                  We couldn't verify your payment. If you believe this is an error,
                  please contact our support team.
                </p>
                <Link to="/">
                  <Button className="bg-gold hover:bg-gold/90 text-white">
                    Return to Home
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PaymentSuccessPage;
